// @flow
import { AFFORDABLE_PROGRAMS } from './affordable';
import HUD_SUBSIDY_CODES from '../constants/hudSubsidyCodes';

type HousingAssistanceLogic = {
  visible: boolean,
  defaultValue: boolean,
  editable: boolean,
  tooltip: ?React$Element<any> | ?string,
};

type PropertyAffordableProgram = {
  masterAffordableProgram?: {
    name?: string,
  },
};

type Property = {
  pap?: Array<PropertyAffordableProgram>,
  config?: {
    housingAssistanceVouchers?: boolean,
  },
  setup?: {
    hudPrimarySubsidyType?: {
      code?: string,
    },
    hudSecondarySubsidyType?: {
      code?: string,
    },
  },
};

type PropertyProgramState = {
  hasRd: boolean,
  hasHud: boolean,
  hasStateProgram: boolean,
  isLayered: boolean,
  isSection8: boolean,
  isSection236: boolean,
};

/**
 * Helper to check if HUD subsidy code matches target
 */
const hasHudSubsidyCode = (
  property: Property,
  subsidyCode: string,
): boolean => {
  const hudPrimarySubsidy = property?.setup?.hudPrimarySubsidyType?.code || '';
  const hudSecondarySubsidy =
    property?.setup?.hudSecondarySubsidyType?.code || '';

  return (
    hudPrimarySubsidy === subsidyCode || hudSecondarySubsidy === subsidyCode
  );
};

/**
 * Helper to determine property program characteristics
 */
const getPropertyProgramState = (property: Property): PropertyProgramState => {
  const pap = property.pap || [];
  const propertyProgramNames = pap.map(
    (pap) => pap.masterAffordableProgram?.name,
  );

  const hasRd = propertyProgramNames.includes(AFFORDABLE_PROGRAMS.RD);
  const hasHud = propertyProgramNames.includes(AFFORDABLE_PROGRAMS.HUD);
  const hasStateProgram = propertyProgramNames.some(
    (programName) =>
      programName !== AFFORDABLE_PROGRAMS.HUD &&
      programName !== AFFORDABLE_PROGRAMS.RD,
  );
  const isLayered = pap.length > 1 && (hasHud || hasRd);
  const isSection8 =
    hasHud && hasHudSubsidyCode(property, HUD_SUBSIDY_CODES.SECTION_8);
  const isSection236 =
    hasHud && hasHudSubsidyCode(property, HUD_SUBSIDY_CODES.SECTION_236);

  return {
    hasRd,
    hasHud,
    hasStateProgram,
    isLayered,
    isSection8,
    isSection236,
  };
};

/**
 * Helper to create disabled logic (visible but not editable)
 */
const createDisabledLogic = (
  tooltip: ?React$Element<any> | ?string,
): HousingAssistanceLogic => ({
  visible: true,
  defaultValue: false,
  editable: false,
  tooltip,
});

/**
 * Helper to create enabled logic (visible and editable)
 */
const createEditableLogic = (
  defaultValue: boolean,
): HousingAssistanceLogic => ({
  visible: true,
  defaultValue,
  editable: true,
  tooltip: null,
});

/**
 * Helper to create state program logic (visible, on, not editable)
 */
const createStateProgramLogic = (): HousingAssistanceLogic => ({
  visible: true,
  defaultValue: true,
  editable: false,
  tooltip: null,
});

/**
 * Determine if property should have disabled housing assistance
 */
const shouldBeDisabled = (programState: PropertyProgramState): boolean => {
  const { hasRd, hasHud, hasStateProgram, isLayered, isSection8 } =
    programState;

  return (
    (hasRd && !isLayered) ||
    (isSection8 && !isLayered) ||
    ((hasRd || hasHud) && !hasStateProgram)
  );
};

/**
 * Determine if property is conventional (no affordable programs)
 */
const isConventionalProperty = (
  programState: PropertyProgramState,
): boolean => {
  const { hasRd, hasHud, hasStateProgram, isLayered } = programState;
  return !hasRd && !hasHud && !hasStateProgram && !isLayered;
};

/**
 * Determines whether housing assistance should be shown and its behavior
 * based on property affordable programs, HUD subsidy types, and configuration.
 *
 * This follows the same logic as the Lease Setup housingAssistanceLogic.
 *
 * @param {Object} options - Configuration options
 * @param {Property} options.property - Property object with affordable programs and setup
 * @param {boolean} options.isSubrentEnabled - Whether subrent codes are enabled
 * @param {string} options.hasCommercialFloorPlans - Commercial floor plans status ('ALL', 'SOME', 'NONE')
 * @param {React$Element<any>} options.housingAssistanceDisabledTooltip - Tooltip for disabled state
 * @param {React$Element<any>} options.subrentCodesDisabledTooltip - Tooltip for subrent disabled state
 * @returns {HousingAssistanceLogic} Configuration for housing assistance display and behavior
 */
export const getHousingAssistanceLogic = ({
  property,
  isSubrentEnabled,
  hasCommercialFloorPlans = 'NONE',
  housingAssistanceDisabledTooltip = null,
  subrentCodesDisabledTooltip = null,
}: {
  property: Property,
  isSubrentEnabled: boolean,
  hasCommercialFloorPlans?: string,
  housingAssistanceDisabledTooltip?: ?React$Element<any> | ?string,
  subrentCodesDisabledTooltip?: ?React$Element<any> | ?string,
}): HousingAssistanceLogic => {
  const defaultLogic: HousingAssistanceLogic = {
    visible: false,
    defaultValue: false,
    editable: false,
    tooltip: null,
  };

  if (!property?.pap) {
    return defaultLogic;
  }

  const programState = getPropertyProgramState(property);
  let baseLogic: HousingAssistanceLogic;

  // Commercial properties - always disabled and off
  if (hasCommercialFloorPlans === 'ALL') {
    baseLogic = createDisabledLogic(housingAssistanceDisabledTooltip);
  }
  // RD-only or Section 8-only - always disabled and off
  else if (shouldBeDisabled(programState)) {
    baseLogic = createDisabledLogic(housingAssistanceDisabledTooltip);
  }
  // Affordable non-HUD/RD (state programs like LIHTC) - always disabled and on
  else if (programState.hasStateProgram) {
    baseLogic = createStateProgramLogic();
  }
  // Conventional properties - always editable
  else if (isConventionalProperty(programState)) {
    baseLogic = createEditableLogic(false);
  }
  // Layered properties with Section 236 or other layered properties - always editable
  else if (programState.isSection236 || programState.isLayered) {
    baseLogic = createEditableLogic(programState.hasStateProgram);
  }
  // Default case (should not reach here based on logic)
  else {
    baseLogic = defaultLogic;
  }

  // If subrent codes are not enabled and the feature would be visible, show tooltip and disable
  if (!isSubrentEnabled && baseLogic.visible) {
    return {
      ...baseLogic,
      defaultValue: false,
      editable: false,
      tooltip: subrentCodesDisabledTooltip,
    };
  }

  return baseLogic;
};

/**
 * Determines if housing assistance should be shown (visible property from logic)
 *
 * @param {Object} options - Configuration options (same as getHousingAssistanceLogic)
 * @returns {boolean} Whether housing assistance should be shown
 */
export const showHousingAssistance = (options: {
  property: Property,
  isSubrentEnabled: boolean,
  hasCommercialFloorPlans?: string,
  housingAssistanceDisabledTooltip?: ?React$Element<any> | ?string,
  subrentCodesDisabledTooltip?: ?React$Element<any> | ?string,
  floorPlanLevelDetails?: {
    isHUD: boolean,
    isRD: boolean,
  },
  hide: boolean,
}): boolean => {
  // Floorplan level overrides:
  const isHUD = options?.floorPlanLevelDetails?.isHUD || false;
  const isRD = options?.floorPlanLevelDetails?.isRD || false;

  if (isRD || isHUD || options?.hide) {
    return false;
  }

  const logic = getHousingAssistanceLogic(options);
  const propertySetting =
    options.property?.config?.housingAssistanceVouchers || false;

  if (logic.editable) {
    return propertySetting || logic.defaultValue;
  } else {
    return logic.defaultValue;
  }
};
